import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import API_URL from "./config";
import "./FreeAgents.css";

const avg = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
const playerOverall = (p) => {
  const offense = avg([p.close, p.mid, p.three, p.free_throw, p.dunk, p.passing, p.dribbling, p.offensive_iq].map(v => v || 50));
  const defense = avg([p.defensive_iq, p.rebounding, p.strength, p.jumping].map(v => v || 50));
  const athletic = avg([p.speed, p.stamina, p.jumping].map(v => v || 50));
  return Math.round(offense * 0.45 + defense * 0.35 + athletic * 0.2);
};

const MidSeasonFreeAgents = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [posFilter, setPosFilter] = useState("ALL");
  const [signing, setSigning] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [capInfo, setCapInfo] = useState(null);
  const [rosterInfo, setRosterInfo] = useState(null);
  const [rosterSize, setRosterSize] = useState(0);
  const [rosterFull, setRosterFull] = useState(false);

  const activeSaveId = localStorage.getItem("activeSaveId");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const loadStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/saves/${activeSaveId}/mid-season-free-agents`, {
        headers: { "X-User-ID": user._id },
      });

      if (!res.ok) {
        throw new Error("Failed to load free agents");
      }

      const data = await res.json();
      setAgents(data.freeAgents || []);
      setRosterSize(data.teamRosterSize || 0);
      setRosterFull(data.rosterFull || false);
      
      const capSpace = data.capSpace || {};
      setCapInfo({
        availableCap: capSpace.availableCap || 0,
        usedCap: capSpace.usedCap || 0,
        totalCap: capSpace.totalCap || 200,
      });

      const rosterData = data.rosterBreakdown || {};
      setRosterInfo({
        totalRoster: data.teamRosterSize || 0,
        byPosition: rosterData || {},
      });
    } catch (err) {
      // Failed to load free agents
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const handleSignPlayer = async (playerId, playerName) => {
    if (rosterFull) {
      return;
    }

    try {
      setSigning(playerId);
      setSuccessMessage("");

      const res = await fetch(
        `${API_URL}/api/saves/${activeSaveId}/sign-free-agent-simple?player_id=${playerId}`,
        {
          method: "POST",
          headers: { "X-User-ID": user._id },
        }
      );

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || "Failed to sign player");
      }

      const data = await res.json();
      setSuccessMessage(`${playerName} signed to 1-year/$1M contract!`);
      
      // Reload data
      setTimeout(() => loadStatus(), 500);
    } catch (err) {
      // Error signing player
    } finally {
      setSigning(null);
    }
  };

  const positions = ["ALL", "PG", "SG", "SF", "PF", "C"];

  const filtered = agents
    .filter((p) => {
      const name = (p.name || "").toLowerCase();
      const matchSearch = name.includes(search.toLowerCase());
      const matchPos = posFilter === "ALL" || p.position === posFilter;
      return matchSearch && matchPos;
    })
    .sort((a, b) => b.overall - a.overall);

  const capPercentage = capInfo ? Math.min(100, (capInfo.usedCap / capInfo.totalCap) * 100) : 0;

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "var(--color-bg-main)" }}>
        <Navbar />
        <div style={{ 
          maxWidth: "1400px", 
          margin: "0 auto", 
          padding: "2rem 1.5rem", 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "center",
          minHeight: "calc(100vh - 80px)"
        }}>
          <div style={{ textAlign: "center" }}>
            <div style={{
              width: "60px",
              height: "60px",
              border: "4px solid rgba(18, 82, 210, 0.2)",
              borderTop: "4px solid #1252D2",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 1.5rem"
            }} />
            <p style={{ 
              fontSize: "1.1rem", 
              color: "var(--color-text-secondary)",
              margin: 0,
              marginBottom: "0.5rem"
            }}>
              Loading free agents...
            </p>
            <p style={{ 
              fontSize: "0.9rem", 
              color: "var(--color-text-secondary)", 
              margin: 0,
              opacity: 0.7
            }}>
              Fetching available players
            </p>
          </div>
        </div>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--color-bg-main)" }}>
      <Navbar />
      
      <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "2rem 1.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "2rem" }}>
          <div>
            <h1 style={{ fontSize: "2.5rem", fontWeight: "700", margin: 0, marginBottom: "0.5rem", color: "var(--color-text-main)" }}>
              Mid-Season Free Agency
            </h1>
            <p style={{ color: "var(--color-text-secondary)", margin: 0 }}>
              <span style={{ color: "#10b981", fontWeight: "600" }}>💰 $1M/year contracts</span>
              {" · "}{agents.length} players available
            </p>
          </div>
          <button 
            onClick={() => navigate("/dashboard")}
            style={{
              padding: "0.75rem 1.5rem",
              background: "var(--color-bg-container)",
              border: "1px solid var(--color-border-input)",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "1rem",
              fontWeight: "600",
              color: "var(--color-text-main)"
            }}
          >
            Back to Dashboard
          </button>
        </div>

        {successMessage && (
          <div style={{
            background: "rgba(34, 197, 94, 0.15)",
            border: "1px solid rgba(34, 197, 94, 0.5)",
            color: "#86efac",
            padding: "1rem 1.5rem",
            borderRadius: "8px",
            marginBottom: "1.5rem",
            fontSize: "0.95rem",
            animation: "slideIn 0.3s ease"
          }}>
            {successMessage}
          </div>
        )}

        {/* Cap Space & Roster Info Row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2rem", marginBottom: "2rem" }}>
          {/* Cap Space Card */}
          <div style={{
            background: "var(--color-bg-container)",
            borderRadius: "8px",
            padding: "1.5rem",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            border: "1px solid var(--color-border-input)"
          }}>
            <h3 style={{ margin: "0 0 1rem 0", fontSize: "1.1rem", fontWeight: "600", color: "var(--color-text-main)" }}>Salary Cap Space</h3>
            <div style={{
              background: "var(--color-textinput-background)",
              borderRadius: "4px",
              height: "24px",
              overflow: "hidden",
              marginBottom: "0.75rem"
            }}>
              <div style={{
                height: "100%",
                width: `${capPercentage}%`,
                background: capPercentage > 80 ? "#ef4444" : capPercentage > 50 ? "#f97316" : "#10b981",
                transition: "width 0.3s ease"
              }} />
            </div>
            <div style={{ fontSize: "0.9rem", color: "var(--color-text-secondary)", lineHeight: "1.5" }}>
              <div style={{ color: "var(--color-text-main)" }}><strong>${capInfo?.usedCap?.toFixed(1) || 0}M</strong> / ${capInfo?.totalCap || 200}M used</div>
              <div style={{ color: "#10b981", fontWeight: "600" }}>
                ${capInfo?.availableCap?.toFixed(1) || 0}M available
              </div>
            </div>
          </div>

          {/* Roster Breakdown Card */}
          <div style={{
            background: "var(--color-bg-container)",
            borderRadius: "8px",
            padding: "1.5rem",
            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
            border: "1px solid var(--color-border-input)"
          }}>
            <h3 style={{ margin: "0 0 1rem 0", fontSize: "1.1rem", fontWeight: "600", color: "var(--color-text-main)" }}>Roster Breakdown</h3>
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: "0.75rem"
            }}>
              {["PG", "SG", "SF", "PF", "C"].map(pos => (
                <div key={pos} style={{
                  textAlign: "center",
                  padding: "0.75rem",
                  background: "var(--color-textinput-background)",
                  borderRadius: "4px"
                }}>
                  <div style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)", marginBottom: "0.25rem", fontWeight: "600" }}>{pos}</div>
                  <div style={{ fontSize: "1.5rem", fontWeight: "700", color: "var(--color-text-main)" }}>
                    {rosterInfo?.byPosition?.[pos] || 0}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginTop: "1rem", fontSize: "0.9rem", color: "var(--color-text-secondary)" }}>
              Total: <strong>{rosterSize}/15</strong>
            </div>
          </div>
        </div>

        {rosterFull && (
          <div style={{
            background: "rgba(239, 68, 68, 0.15)",
            border: "2px solid rgba(239, 68, 68, 0.5)",
            color: "#fca5a5",
            padding: "1rem 1.5rem",
            borderRadius: "8px",
            marginBottom: "2rem",
            textAlign: "center",
            fontWeight: "600"
          }}>
            Your roster is at maximum capacity (15 players). Cannot sign additional players.
          </div>
        )}

        {/* Search & Filters */}
        <div style={{
          background: "var(--color-bg-container)",
          borderRadius: "8px",
          padding: "1.5rem",
          marginBottom: "2rem",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid var(--color-border-input)"
        }}>
          <input
            className="fa-search"
            placeholder="Search players..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: "100%",
              padding: "0.75rem",
              border: "1px solid var(--color-border-input)",
              borderRadius: "6px",
              fontSize: "1rem",
              marginBottom: "1rem",
              boxSizing: "border-box",
              background: "var(--color-textinput-background)",
              color: "var(--color-text-main)"
            }}
          />
          <div className="fa-pos-filters" style={{
            display: "flex",
            gap: "0.75rem",
            flexWrap: "wrap"
          }}>
            {positions.map(pos => (
              <button
                key={pos}
                className={`fa-pos-btn ${posFilter === pos ? "active" : ""}`}
                onClick={() => setPosFilter(pos)}
                style={{
                  padding: "0.5rem 1rem",
                  background: posFilter === pos ? "var(--color-primary)" : "var(--color-bg-container)",
                  color: posFilter === pos ? "white" : "var(--color-text-secondary)",
                  border: posFilter === pos ? "1px solid var(--color-primary)" : "1px solid var(--color-border-input)",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: "0.9rem"
                }}
              >
                {pos}
              </button>
            ))}
          </div>
        </div>

        {/* Players Grid */}
        {filtered.length === 0 ? (
          <div style={{
            background: "var(--color-bg-container)",
            borderRadius: "8px",
            padding: "3rem",
            textAlign: "center",
            color: "var(--color-text-secondary)",
            border: "2px dashed var(--color-border-input)"
          }}>
            {rosterFull ? "Your roster is full." : "No free agents available."}
          </div>
        ) : (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
            gap: "1.5rem"
          }}>
            {filtered.map((agent) => (
              <div key={agent._id} style={{
                background: "var(--color-bg-container)",
                border: "1px solid var(--color-border-input)",
                borderRadius: "8px",
                padding: "1rem",
                transition: "all 0.2s ease",
                cursor: rosterFull ? "not-allowed" : "pointer"
              }}
              onMouseEnter={(e) => {
                if (!rosterFull) {
                  e.currentTarget.style.transform = "translateY(-4px)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)";
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}>
                <div style={{ marginBottom: "1rem" }}>
                  <div style={{ fontSize: "1.1rem", fontWeight: "700", color: "var(--color-text-main)", marginBottom: "0.5rem" }}>
                    {agent.name}
                  </div>
                  <div style={{ fontSize: "0.9rem", color: "var(--color-text-secondary)", marginBottom: "0.25rem" }}>
                    {agent.position}
                  </div>
                  <div style={{ fontSize: "0.85rem", color: "var(--color-text-secondary)" }}>
                    {agent.experience} yrs exp
                  </div>
                </div>
                
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingTop: "0.75rem",
                  borderTop: "1px solid var(--color-border-input)",
                  marginBottom: "1rem"
                }}>
                  <div style={{ fontSize: "0.85rem", color: "var(--color-text-secondary)" }}>OVR</div>
                  <div style={{
                    fontSize: "1.5rem",
                    fontWeight: "700",
                    color: "var(--color-primary)"
                  }}>
                    {agent.overall}
                  </div>
                </div>

                <button
                  onClick={() => handleSignPlayer(agent._id, agent.name)}
                  disabled={rosterFull || signing === agent._id}
                  style={{
                    width: "100%",
                    padding: "0.75rem",
                    background: rosterFull ? "rgba(107, 114, 128, 0.5)" : "#10b981",
                    color: rosterFull ? "#9ca3af" : "white",
                    border: "none",
                    borderRadius: "6px",
                    fontWeight: "600",
                    fontSize: "0.95rem",
                    cursor: rosterFull || signing === agent._id ? "not-allowed" : "pointer",
                    opacity: rosterFull ? 0.6 : 1,
                    transition: "all 0.2s ease"
                  }}
                  onMouseEnter={(e) => {
                    if (!rosterFull && signing !== agent._id) {
                      e.currentTarget.style.background = "#059669";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!rosterFull) {
                      e.currentTarget.style.background = "#10b981";
                    }
                  }}
                >
                  {signing === agent._id ? "Signing..." : "Sign"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MidSeasonFreeAgents;
